import React, { useCallback, useMemo, useState } from 'react';
import Image from 'next/image';
import styles from './index.module.less';
import { InsProfileModel } from '@/types/home';
import { formatNumbers, imgUrl } from '@/shared/utils';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import { insFamousRefresh } from '@/lib/service';
import { Affix, message, Tooltip } from 'antd';
import { useUserProvider } from '@/context/UserProvider';
import { useWindowSize } from 'react-use';
import { QuestionCircleFilled, QuestionCircleOutlined } from '@ant-design/icons';

const SocialProfileCard: React.FC<{
  data?: InsProfileModel;
  bottomVisible?: boolean;
  onAddAccount?: () => void;
}> = ({ data, bottomVisible, onAddAccount }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const { isLogin, onOpenLoginModal, onChangedLoginView } = useUserProvider();
  const { width } = useWindowSize();

  const getTimestamp = useMemo(() => {
    const currentTime = new Date().getTime();
    const lastTime = new Date(data?.last_update_time).getTime();

    const timeDiff = currentTime - lastTime;
    if (timeDiff > 86400000) {
      return `${Math.floor(timeDiff / 86400000)} ${t('days ago updated')}`;
    } else if (timeDiff > 3600000) {
      const hours = Math.floor(timeDiff / 3600000);
      const minutes = Math.floor((timeDiff % 3600000) / 60000);
      return `${hours} ${t('hours ago updated')}`;
    } else {
      return `${Math.floor(timeDiff / 60000)} ${t('minutes ago updated')}`;
    }
  }, [data?.last_update_time]);

  const mediaList = useMemo(() => {
    const tiktokLink = data?.social_media?.tiktok;
    const youtubeLink = data?.social_media?.youtube;
    const twitterLink = data?.social_media?.twitter;
    const facebookLink = data?.social_media?.facebook;

    return [
      {
        imgPath:
          tiktokLink?.length > 0 ? `/images/starInfo/tiktok-icon.webp` : `/images/starInfo/tiktok-icon-disabled.webp`,
        link: tiktokLink,
      },
      {
        imgPath:
          youtubeLink?.length > 0
            ? `/images/starInfo/youtube-icon.webp`
            : `/images/starInfo/youtube-icon-disabled.webp`,
        link: youtubeLink,
      },
      {
        imgPath: twitterLink?.length > 0 ? `/images/starInfo/x-icon.webp` : `/images/starInfo/x-icon-disabled.webp`,
        link: twitterLink,
      },
      {
        imgPath:
          facebookLink?.length > 0
            ? `/images/starInfo/facebook-icon.webp`
            : `/images/starInfo/facebook-icon-disabled.webp`,
        link: facebookLink,
      },
    ];
  }, [data?.social_media]);

  const getAdContent = useMemo(() => {
    if (width < 900 || bottomVisible) {
      return null;
    }
    return (
      <Affix offsetTop={isLogin ? 16 : 57}>
        <div className={styles.featuresSection}>
          <div className={styles.featuresTitle}>
            {t("Track Influencers' Latest Instagram Activities & Get the Gossip!")}
          </div>
          <ul className={styles.featuresList}>
            {[
              '100,000+ of the Hottest Influencer Accounts',
              'In-depth Reports: See their latest followers, likes, and more',
              'AI-Powered Insights: Detect suspicious activities, analyze interests, locations, and more',
              'Free Anonymous Story Viewer: View disappearing Instagram stories anonymously',
            ].map((feature, index) => (
              <li key={index} className={styles.featureItem}>
                <Image
                  src={'/images/starInfo/right-icon.webp'}
                  alt={''}
                  width={16}
                  height={16}
                  style={{ marginTop: '3px', height: 16, width: 16 }}
                />
                <span>{t(feature)}</span>
              </li>
            ))}
          </ul>
          <button className={styles.gradientButton} onClick={onAddAccount}>
            {t('Try Dolphin Radar Now')}
          </button>
        </div>
      </Affix>
    );
  }, [isLogin, width, bottomVisible]);

  const onRefresh = () => {
    if (!isLogin) {
      onChangedLoginView(false);
      onOpenLoginModal(true);
      return;
    }
    if (loading) {
      return;
    }
    setLoading(true);
    insFamousRefresh(data?.media_name)
      .then((res) => {
        if (res.code === 0) {
          message.success(
            t("Your update request has been received! We're working on bringing you the latest site information.")
          );
        } else {
          message.error(res?.message);
        }
      })
      .catch((error) => {
        message.error(error?.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const getContent = useCallback(() => {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.cardContent}>
            <div className={styles.profileSection}>
              <div className={styles.avatarContainer}>
                <Image
                  src={imgUrl(data?.avatar)}
                  alt={data?.media_name}
                  className={styles.avatar}
                  width={114}
                  height={114}
                />
                {/* <div className={styles.instagramIcon}>
                  <Image src="/images/starInfo/instagram-icon.webp" alt="" width={32} height={32} />
                </div> */}
              </div>

              <div>
                <div className={styles.username}>{data?.nickname}</div>
                <div className="flex justify-center items-center gap-1">
                  <Link
                    href={`https://www.instagram.com/${data?.media_name}`}
                    target="_blank"
                    rel="nofollow noreferrer"
                  >
                    <p className={styles.handle}>@{data?.media_name}</p>
                  </Link>
                  <Tooltip title={t('You can tap the username to view more on Instagram.')}>
                    <QuestionCircleFilled className="text-sm text-[#8E8E8E] cursor-pointer" />
                  </Tooltip>
                </div>
              </div>
            </div>

            <div className={styles.followers}>
              <span className={styles.followerCount}>{formatNumbers(data?.follower_num)}</span> {t('followers')}
            </div>

            <div className={styles.lastUpdated}>
              <Image
                src={'/images/starInfo/refresh-data-icon.webp'}
                alt={'updated'}
                width={20}
                height={20}
                style={{ cursor: 'pointer' }}
                onClick={onRefresh}
              />
              <span>{getTimestamp}</span>
            </div>

            <div className={styles.otherAccounts}>
              <p className={styles.otherAccountsTitle}>
                {t("@x111x's other accounts").replace('x111x', data?.nickname)}
              </p>
              <div className={styles.accountIcons}>
                {mediaList?.map((platform) => (
                  <div
                    key={platform.imgPath}
                    className={styles.accountIcon}
                    onClick={() => {
                      if (platform.link) {
                        window.open(platform.link, '_blank');
                      }
                    }}
                  >
                    <Image src={platform.imgPath} alt={platform.link} width={28} height={28} />
                  </div>
                ))}
              </div>
            </div>

            <button className={styles.button} onClick={onAddAccount}>
              {t('+ Add to Track Updates')}
            </button>

            {getAdContent}
          </div>
        </div>
      </div>
    );
  }, [data, bottomVisible, width]);

  return <>{getContent()}</>;
};

export default SocialProfileCard;
